/**
 * Weekly offs may use only the days left after fixed working days.
 * allowance = days in the month − working days.
 * Weekday indexes match the backend: Monday = 0 … Sunday = 6.
 */

export const MONTH_LENGTHS = [
  { days: 28, label: "28-day month" },
  { days: 29, label: "29-day month" },
  { days: 30, label: "30-day month" },
  { days: 31, label: "31-day month" },
];

export function weeklyOffAllowance(daysInMonth, workingDays) {
  const month = Number(daysInMonth);
  const working = Number(workingDays);
  if (!Number.isFinite(month)) return 0;
  if (!Number.isFinite(working) || working <= 0) return month;
  return Math.max(0, month - working);
}

/** Fewest times these weekdays occur in a month of this length. */
export function minOffsInMonth(weekdayIndexes, daysInMonth) {
  const set = new Set(weekdayIndexes);
  if (set.size === 0) return 0;
  let min = Infinity;
  for (let start = 0; start < 7; start += 1) {
    let count = 0;
    for (let day = 0; day < daysInMonth; day += 1) {
      if (set.has((start + day) % 7)) count += 1;
    }
    if (count < min) min = count;
  }
  return min;
}

export function weeklyOffPatternError(weekdayIndexes, workingDays, extraOffs = 0) {
  const fixed = Number(workingDays);
  if (!Number.isFinite(fixed) || fixed <= 0) return null;
  const allowance = weeklyOffAllowance(31, fixed);
  const least = minOffsInMonth(weekdayIndexes, 31) + Number(extraOffs || 0);
  if (least <= allowance) return null;
  const left30 = weeklyOffAllowance(30, fixed);
  const left28 = weeklyOffAllowance(28, fixed);
  return (
    `This weekly off needs at least ${least} days in a 31-day month. ` +
    `Working days are fixed at ${fixed}, so only ${allowance} day(s) remain (31 − ${fixed}). ` +
    `A 30-day month leaves ${left30}, and a 28-day month leaves ${left28}. ` +
    "Choose fewer weekly off days."
  );
}

/** Months where the pattern is trimmed so working days stay fixed. */
export function shortMonthTrimNote(weekdayIndexes, workingDays, extraOffs = 0) {
  const fixed = Number(workingDays);
  if (!Number.isFinite(fixed) || fixed <= 0) return null;
  const parts = [];
  for (const { days, label } of MONTH_LENGTHS) {
    if (days === 31) continue;
    const allowance = weeklyOffAllowance(days, fixed);
    const least = minOffsInMonth(weekdayIndexes, days) + Number(extraOffs || 0);
    if (least > allowance) {
      parts.push(`${label} keeps ${allowance}`);
    }
  }
  if (!parts.length) return null;
  return (
    `Working days stay ${fixed}. When the chosen days are more than the days left, ` +
    `only the earliest ones stay as weekly offs (${parts.join("; ")}). ` +
    "The later ones are working days."
  );
}
